import { action, computed, observable } from "mobx";

import type { Event, Positions, Resources, Usage } from "@mainion/protocol";

import { Worker, workerKey } from "./Worker.ts";

/** Every worker on every controller, built up from the events the controllers send. */
export class WorkerStore {
    @observable accessor workers = new Map<string, Worker>();
    @observable accessor controllers = new Set<string>();
    /** What each controller's server has, from the last time it started. */
    @observable accessor resources = new Map<string, Resources>();
    /** For each controller, the last event applied. Sent along when reconnecting, so only new events come in. */
    readonly positions: Positions = {};

    /** Newest first. */
    @computed get list(): Worker[] {
        return [...this.workers.values()].sort((a, b) => b.startedAt.localeCompare(a.startedAt));
    }

    /** What the LLM calls of every worker that reports it used so far. */
    @computed get usages(): Usage[] {
        return this.list.flatMap((worker) => (worker.usage ? [worker.usage] : []));
    }

    /** How many of a controller's workers are still running. */
    busy(controller: string): number {
        return this.list.filter((worker) => worker.controller === controller && worker.running).length;
    }

    @action addController(name: string): void {
        this.controllers.add(name);
    }

    @action apply(event: Event): void {
        if (event.seq <= (this.positions[event.controller] ?? 0)) return;
        this.positions[event.controller] = event.seq;
        this.controllers.add(event.controller);

        if (event.type === "controller.started") {
            const { cpus, memory, disk, maxWorkers } = event;

            this.resources.set(event.controller, { cpus, memory, disk, maxWorkers });

            return;
        }

        const key = workerKey(event.controller, event.worker);
        let worker = this.workers.get(key);

        if (!worker) {
            worker = new Worker(event.controller, event.worker);
            this.workers.set(key, worker);
        }

        switch (event.type) {
            case "worker.started":
                worker.image = event.image;
                worker.command = event.command;
                worker.startedAt = event.time;
                break;
            case "worker.output":
                worker.lines.push({ stream: event.stream, text: event.text });
                break;
            case "worker.progress":
                worker.progress = { message: event.message, done: event.done, total: event.total };
                break;
            case "worker.usage":
                worker.usage = { model: event.model, input: event.input, output: event.output, cost: event.cost };
                break;
            case "worker.exited":
                worker.exitCode = event.exitCode;
                break;
        }
    }
}
